import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Avatar,
  CircleLoader,
  toast
} from '@sparrowengg/twigs-react';
import { fetchEnvironments } from '../utils/environmentService';
import { useDispatch, useSelector } from 'react-redux';
import { setEnvironment, clearEnvironment } from '../redux/environmentSlice';

// Updated paths for sparrow icons
const sparrowIcons = [
  '/assets/sparrows/dove.png',
  '/assets/sparrows/carrier-pigeon.png',
  '/assets/sparrows/message.png',
  '/assets/sparrows/sound.png',
  '/assets/sparrows/flying.png',
  '/assets/sparrows/carrier-pigeon (1).png',
  '/assets/sparrows/dove (1).png',
  '/assets/sparrows/envelope.png',
  '/assets/sparrows/flying (1).png'
];

// S3 URL for SS Logo
const SS_LOGO_URL = '/assets/SSLogo.png';

function EnvironmentSelector({ onEnvironmentSelected, environments, setEnvironments }) {
  const dispatch = useDispatch();
  const selectedEnv = useSelector(state => state.environment.selectedEnvironment);
  const [loading, setLoading] = useState(true);

  const loadEnvironments = useCallback(async () => {
    try {
      setLoading(true);
      const envData = await fetchEnvironments();
      setEnvironments(envData);
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error Loading Environments',
        description: 'Failed to load environment configurations. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [setEnvironments]);

  useEffect(() => {
    loadEnvironments();
  }, [loadEnvironments]);

  const handleEnvironmentSelect = (environmentName) => {
    const endpoint = environments[environmentName];
    if (endpoint) {
      dispatch(setEnvironment({ endpoint, environmentName }));
      toast({
        variant: 'success',
        title: 'Environment Selected',
        description: `Connected to ${environmentName} environment`,
      });
      if (onEnvironmentSelected) {
        onEnvironmentSelected(endpoint, environmentName);
      }
    }
  };

  // Helper to get a random icon for each card
  const getRandomSparrowIcon = (envName) => {
    // Use envName to get a deterministic random icon per environment
    let hash = 0;
    for (let i = 0; i < envName.length; i++) {
      hash = envName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % sparrowIcons.length;
    return sparrowIcons[idx];
  };

  const getEnvironmentCards = () => {
    return Object.entries(environments).map(([envName, endpoint]) => (
      <Box
        key={envName}
        as="button"
        onClick={() => handleEnvironmentSelect(envName)}
        css={{
          cursor: 'pointer',
          padding: '$6',
          border: selectedEnv === envName ? '2px solid $primary500' : '1px solid $black200',
          backgroundColor: selectedEnv === envName ? '$primary50' : '$white',
          borderRadius: '$lg',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: '$primary300',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-2px)'
          },
          '&:focus': {
            outline: 'none',
            borderColor: '$primary500',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
          },
          minHeight: '120px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: '$3',
          width: '100%'
        }}
      >
        <Box css={{
          padding: '$3',
          borderRadius: '$md',
          backgroundColor: selectedEnv === envName ? '$primary100' : '$black50',
          border: selectedEnv === envName ? '1px solid $primary200' : '1px solid $black200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '48px',
          width: '48px',
          margin: '0 auto',
        }}>
          <img src={getRandomSparrowIcon(envName)} alt="Sparrow Icon" style={{ width: 32, height: 32, objectFit: 'contain' }} />
        </Box>
        
        <Box>
          <Text 
            size="lg" 
            css={{ 
              fontWeight: 'bold', 
              color: selectedEnv === envName ? '$primary700' : '$black800',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '$1',
              fontFamily: 'DM Sans'
            }}
          >
            {envName}
          </Text>
        </Box>
      </Box>
    ));
  };

  if (loading) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        css={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, $primary50 0%, $white 100%)'
        }}
      >
        <CircleLoader size="3xl" color="primary" />
        <Text css={{ marginTop: '$4', color: '$black600' }}>
          Loading environments...
        </Text>
      </Flex>
    );
  }

  return (
    <Box css={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, $primary50 0%, $white 100%)',
      padding: '$8'
    }}>
      {/* Header */}
      <Flex
        direction="column"
        alignItems="center"
        css={{
          maxWidth: '1200px',
          margin: '0 auto',
          marginBottom: '$12'
        }}
      >
        <Flex alignItems="center" css={{ marginBottom: '$6' }}>
          <Box 
            as="button"
            onClick={() => dispatch(clearEnvironment())}
            css={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0
            }}
          >
            <Avatar
              src={SS_LOGO_URL}
              alt="SparrowConnect Logo"
              size="lg"
              css={{
                border: '3px solid $primary200',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                marginRight: '$4'
              }}
            />
            <Text
              size="lg"
              css={{
                fontWeight: 'bold',
                color: '$black800',
                letterSpacing: '-0.02em',
                fontFamily: 'DM Sans'
              }}
            >
              SparrowConnect
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Environment Grid */}
      <Box css={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <Box css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '$6',
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: '$4'
          }
        }}>
          {getEnvironmentCards()}
        </Box>
      </Box>
    </Box>
  );
}

export default EnvironmentSelector; 